import { COMMON } from '../../../strings/common';
import { capitalize } from 'lodash-es';
import commonBaseService from '../../commonBaseService';

const STATUSES_MAP = {
	submitted: 'submitted',
	pending: 'pending',
	partiallyCompleted: 'partiallyCompleted',
	completed: 'completed',
	rejected: 'rejected',
	cancelled: 'cancelled',
};

const ICON_MAP = {
	submitted: 'IvOrderSubmitted',
	pending: 'IvOrderPending',
	partiallyCompleted: 'IvOrderPending',
	completed: 'IvOrderCompleted',
	rejected: 'IvOrderRejected',
	cancelled: 'IvOrderCancelled',
};

const CANCEL_ORDER_ALLOWED_STATUSES = [
	STATUSES_MAP.submitted,
	STATUSES_MAP.rejected,
	STATUSES_MAP.cancelled,
];

const NEW_ORDER_TITLE = {
	[STATUSES_MAP.submitted]: 'Order submitted',
	[STATUSES_MAP.pending]: 'Order pending',
	[STATUSES_MAP.partiallyCompleted]: 'Order partially completed',
	[STATUSES_MAP.completed]: 'Order completed',
	[STATUSES_MAP.rejected]: 'Order rejected',
	[STATUSES_MAP.cancelled]: 'Order cancelled',
};

const NEW_ORDER_FRIENDLY_STATUS = {
	[STATUSES_MAP.submitted]: 'submitted',
	[STATUSES_MAP.pending]: 'pending for execution',
	[STATUSES_MAP.partiallyCompleted]: 'partially completed',
	[STATUSES_MAP.completed]: 'executed',
	[STATUSES_MAP.rejected]: 'rejected',
	[STATUSES_MAP.cancelled]: 'cancelled',
};

const NEW_ORDER_ICON_MAP = {
	[STATUSES_MAP.submitted]: ICON_MAP[STATUSES_MAP.submitted],
	[STATUSES_MAP.pending]: ICON_MAP[STATUSES_MAP.pending],
	[STATUSES_MAP.partiallyCompleted]:
		ICON_MAP[STATUSES_MAP.partiallyCompleted],
	[STATUSES_MAP.completed]: ICON_MAP[STATUSES_MAP.completed],
	[STATUSES_MAP.rejected]: ICON_MAP[STATUSES_MAP.rejected],
	[STATUSES_MAP.cancelled]: ICON_MAP[STATUSES_MAP.cancelled],
};

const MODIFY_ORDER_TITLE = {
	[STATUSES_MAP.submitted]: 'Modification request submitted',
	[STATUSES_MAP.pending]: 'Order modified',
	[STATUSES_MAP.partiallyCompleted]: 'Order modified',
	[STATUSES_MAP.completed]: NEW_ORDER_TITLE[STATUSES_MAP.completed],
	[STATUSES_MAP.rejected]: 'Modification rejected',
	[STATUSES_MAP.cancelled]: NEW_ORDER_TITLE[STATUSES_MAP.cancelled],
};

const MODIFY_ORDER_FRIENDLY_STATUS = {
	[STATUSES_MAP.submitted]: 'submitted',
	[STATUSES_MAP.pending]: 'modified',
	[STATUSES_MAP.partiallyCompleted]: 'modified',
	[STATUSES_MAP.completed]: NEW_ORDER_FRIENDLY_STATUS[STATUSES_MAP.completed],
	[STATUSES_MAP.rejected]: 'rejected',
	[STATUSES_MAP.cancelled]: NEW_ORDER_FRIENDLY_STATUS[STATUSES_MAP.cancelled],
};

const MODIFY_ORDER_ICON_MAP = {
	[STATUSES_MAP.submitted]: ICON_MAP[STATUSES_MAP.submitted],
	[STATUSES_MAP.pending]: ICON_MAP[STATUSES_MAP.completed],
	[STATUSES_MAP.rejected]: ICON_MAP[STATUSES_MAP.rejected],
	[STATUSES_MAP.cancelled]: ICON_MAP[STATUSES_MAP.cancelled],
	[STATUSES_MAP.completed]: ICON_MAP[STATUSES_MAP.completed],
	[STATUSES_MAP.partiallyCompleted]: ICON_MAP[STATUSES_MAP.completed],
};

const CANCEL_ORDER_TITLE = {
	[STATUSES_MAP.submitted]: 'Request placed',
	[STATUSES_MAP.pending]: 'Request placed',
	[STATUSES_MAP.partiallyCompleted]: 'Request placed',
	[STATUSES_MAP.completed]: 'Request placed',
	[STATUSES_MAP.rejected]: 'Order rejected',
	[STATUSES_MAP.cancelled]: 'Order cancelled',
};

const CANCEL_ORDER_ICON_MAP = {
	[STATUSES_MAP.submitted]: ICON_MAP[STATUSES_MAP.completed],
	[STATUSES_MAP.pending]: ICON_MAP[STATUSES_MAP.completed],
	[STATUSES_MAP.partiallyCompleted]: ICON_MAP[STATUSES_MAP.completed],
	[STATUSES_MAP.completed]: ICON_MAP[STATUSES_MAP.completed],
	[STATUSES_MAP.rejected]: ICON_MAP[STATUSES_MAP.rejected],
	[STATUSES_MAP.cancelled]: ICON_MAP[STATUSES_MAP.cancelled],
};

const FLOWS = {
	new_order: 'new_order',
	modify: 'modify',
	cancel: 'cancel',
};

const DEFAULT_TITLE = NEW_ORDER_TITLE[STATUSES_MAP.submitted];
const DEFAULT_ICON = NEW_ORDER_ICON_MAP[STATUSES_MAP.submitted];
const DEFAULT_CONFIG = {
	title: DEFAULT_TITLE,
	icon: DEFAULT_ICON,
	message: '',
};

const getOrderStatusFromFeed = (feedData) => {
	const statusString = feedData.OrderStatus || feedData.Status;
	const status = Number(statusString);
	switch (status) {
		case 1:
		case 2:
		case 3:
		case 4:
		case 12:
		case 13:
		case 14:
		case 16:
		case 18:
		case 21:
		case 22:
			return STATUSES_MAP.submitted;
		case 5:
		case 8:
			return STATUSES_MAP.pending;
		case 9:
		case 10:
		case 11:
		case 15:
			return STATUSES_MAP.rejected;
		case 20:
		case 7:
			if (Number(feedData.PendingQty) > 0 && status === 7) {
				return STATUSES_MAP.partiallyCompleted;
			}
			return STATUSES_MAP.completed;
		case 6:
		case 17:
		case 19:
			return STATUSES_MAP.cancelled;
		default:
			return STATUSES_MAP.submitted;
	}
};

const getNewOrderDisplayMessage = (
	transactionType,
	quantity,
	scripName,
	price,
	status,
) => {
	return `${capitalize(
		transactionType,
	)} order for ${quantity} qty of ${scripName} at ${price} is ${
		NEW_ORDER_FRIENDLY_STATUS[status]
	}`;
};

const getCancelOrderDisplayMessage = (status) => {
	if (status === STATUSES_MAP.submitted) {
		return 'Order cancellation for your order has been placed successfully.';
	}
	return '';
};

const getModifyOrderDisplayMessage = (
	transactionType,
	quantity,
	scripName,
	price,
	status,
) => {
	if (status === STATUSES_MAP.rejected) {
		return '';
	} else if (status === STATUSES_MAP.submitted) {
		return `Order modification for your order has been submitted successfully`;
	} else if (
		status === STATUSES_MAP.completed &&
		status === STATUSES_MAP.cancelled
	) {
		return getNewOrderDisplayMessage(
			transactionType,
			quantity,
			scripName,
			price,
			status,
		);
	} else {
		return `${scripName} order for ${quantity} qty at ${price} is ${MODIFY_ORDER_FRIENDLY_STATUS[status]}.`;
	}
};

const getOrderStatusConfig = ({
	latestFeed = {},
	flow = '',
	transactionType = '',
	quantity = '',
	displayName = '',
	orderPrice = '',
}) => {
	const status = latestFeed?.status;

	if (commonBaseService.isEmptyOrUndefined(latestFeed)) {
		return DEFAULT_CONFIG;
	}

	let title = '';
	let icon = '';
	let message = '';
	switch (flow) {
		case FLOWS.new_order:
			title = NEW_ORDER_TITLE[status];
			icon = NEW_ORDER_ICON_MAP[status];
			message = getNewOrderDisplayMessage(
				transactionType,
				quantity,
				displayName,
				orderPrice,
				status,
			);
			break;
		case FLOWS.modify:
			title = MODIFY_ORDER_TITLE[status];
			icon = MODIFY_ORDER_ICON_MAP[status];
			message = getModifyOrderDisplayMessage(
				transactionType,
				quantity,
				displayName,
				orderPrice,
				status,
			);
			break;
		case FLOWS.cancel:
			title = CANCEL_ORDER_TITLE[status];
			icon = CANCEL_ORDER_ICON_MAP[status];
			message = getCancelOrderDisplayMessage(status);
			break;
		default:
			title = NEW_ORDER_TITLE[status];
			icon = NEW_ORDER_ICON_MAP[status];
			break;
	}

	return {
		title: title || DEFAULT_TITLE,
		icon: icon || DEFAULT_ICON,
		message,
	};
};

const getRequestPlacedCtaText = (isModifyOrSip) => {
	return isModifyOrSip ? COMMON.GO_TO_ORDERS : COMMON.OKAY;
};

const getOrderPlacedCtaText = (isModifyOrSip) => {
	return isModifyOrSip ? COMMON.OKAY : COMMON.GO_TO_ORDERS;
};

export const orderConfirmationService = {
	getOrderStatusConfig,
	getOrderStatusFromFeed,
	getRequestPlacedCtaText,
	getOrderPlacedCtaText,
	CANCEL_ORDER_ALLOWED_STATUSES,
	FLOWS,
	STATUSES_MAP,
};
